/**
 * The Priorities view's words — zone names and hints, the insights rail's
 * kind names, nouns and prompts — as PM defaults plus the tenant override
 * contract (`prioritiesCopy`). Pure: no colours, no UI imports, so the
 * scoring and insight modules (and their node tests) can read it. Colours
 * live in zoneStyles.js, which layers them onto these.
 */

export const ZONE_TEXT = {
  fires: { label: "Fires", hint: "Severe and growing. Act this cycle." },
  chronic: { label: "Chronic", hint: "Severe, not moving. Known pain. Roadmap it; it will not fix itself." },
  resolving: { label: "Resolving", hint: "Severe, but shrinking. Did the fix land, or did the audience change? Validate before closing." },
  emerging: { label: "Emerging", hint: "Minor today, gaining share. Watch; re-check next period." },
  background: { label: "Background", hint: "Minor and stable. The hum of the product. Ignore unless a filter makes it severe." },
  fading: { label: "Fading", hint: "Minor and going away. Archive." },
};

// A tenant's `prioritiesCopy.zones.<zone>.{label,hint}` wins per field.
export const resolveZoneText = (copy) =>
  Object.fromEntries(
    Object.entries(ZONE_TEXT).map(([key, text]) => {
      const o = copy?.zones?.[key];
      return [key, o ? { label: o.label ?? text.label, hint: o.hint ?? text.hint } : text];
    })
  );

// The insights rail's wording: a name per insight kind, the nouns the
// templates speak in, and the one-line prompts some kinds end on. Override
// contract: `prioritiesCopy.insights.{kinds,verbs,nouns,prompts}`.
const INSIGHT_COPY = {
  kinds: {
    caughtFire: "Caught fire",
    zoneMove: "Moved",
    resolving: "Resolving",
    goneSilent: "Gone silent",
    concentration: "Concentration",
    channelContrast: "Channel contrast",
    escalation: "Escalation",
    breadthDepth: "Breadth vs depth",
    winSlipping: "Win slipping",
  },
  verbs: { caughtFire: "caught fire" },
  nouns: {
    observation: "observation",
    observations: "observations",
    conversation: "conversation",
    conversations: "conversations",
    period: "period",
    periods: "periods",
    subTopic: "sub-topic",
    subTopics: "sub-topics",
  },
  prompts: {
    resolving: "Did the fix land, or did those voices go quiet?",
    goneSilent: "Fixed, or unheard?",
    loud: "One loud voice, not a pattern yet.",
    quiet: "A quiet consensus — widespread, never loud.",
  },
};

export const resolveInsightCopy = (copy) => {
  const o = copy?.insights || {};
  return {
    kinds: { ...INSIGHT_COPY.kinds, ...(o.kinds || {}) },
    verbs: { ...INSIGHT_COPY.verbs, ...(o.verbs || {}) },
    nouns: { ...INSIGHT_COPY.nouns, ...(o.nouns || {}) },
    prompts: { ...INSIGHT_COPY.prompts, ...(o.prompts || {}) },
  };
};

/**
 * The Ask AI opening lines, per surface.
 *
 * `dashboard` — the widget on the New tab. The dashboard around it answers
 * *what* moved; it cannot answer *why*, because the why is in the
 * observations. That division is the point of the widget, so it is what the
 * copy says. Not a greeting: a name in a headline tells the reader nothing
 * about what the panel is for.
 *
 * `chat` — the empty Ask AI page, which has nothing around it to divide
 * labour with, so it opens the way a chat does. `{name}` is replaced with
 * the reader's first name, and `title` is used when there isn't one.
 *
 * Override contract: `prioritiesCopy.askAI.<surface>.{title,titleWithName,subtitle}`.
 */
const ASK_AI_COPY = {
  dashboard: {
    title: "The dashboard shows what moved. Ask why.",
    subtitle: "Start with a question below, or write your own — answered from the observations behind these numbers.",
  },
  chat: {
    title: "What would you like to know?",
    titleWithName: "Hi {name}, what would you like to know?",
    subtitle: "Pick a question about a topic your customers raise, or ask your own below.",
  },
};

export const resolveAskAICopy = (copy, surface = "dashboard") => ({
  ...ASK_AI_COPY[surface],
  ...(copy?.askAI?.[surface] || {}),
});
